import Highcharts from 'highcharts/highstock'
import {
	intersection as _intersection,
	isEqual as _isEqual,
	map as _map,
	max as _max,
	min as _min,
	values as _values,
	size as _size,
} from 'lodash'

import { COLOUR } from 'components'
import { ArrayHandler, DataHandler, DateTimeHandler, NumberHandler } from 'utils'
import func from './func'
import { CHART_EXPORTING } from './config'


const checkData = (data, arrCheck) => {
	if (arrCheck.length === 0) { return data; }

	let dataUpdated = { ...data };

	for (let i = 0; i < dataUpdated.length; i++) {
		const category = dataUpdated[i];

		arrCheck.forEach((str) => {
			if (category.includes(str)) {
				dataUpdated[`${str}(e)`] = dataUpdated[category];

				delete dataUpdated[category];
			}
		});
	}

	return dataUpdated;
}
const checkDiff = (arr) => (new Set(arr).size !== 1);
const getDashStyle = (style, order) => {
	if (style.toLowerCase() === 'dotline') { return 'dash'; }

	const numSeriesColour = COLOUR.series.dark.length;

	if (order >= numSeriesColour) {
		const dashStyle = [
			'shortDot',
			'dashDot',
			'dot',
			'longDash',
			'longDashDot',
			'longDashDotDot',
			'shortDash',
			'shortDashDot',
			'shortDashDotDot',
		];
		const divided = Math.round(order / numSeriesColour) - 1;

		return dashStyle[divided];
	}

	return 'solid';
};
const parseAdditionalData = (originData, additionalData, refdataArray) => {
	if (!originData || originData.toString().length === 0) { return null; }

	if (Array.isArray(originData)) {
		if (Array.isArray(additionalData)) {
			return [
				NumberHandler.floatFormatter(parseFloat(originData[0]) * (refdataArray.length ? refdataArray[additionalData[0]] : additionalData[0]), 2),
				NumberHandler.floatFormatter(parseFloat(originData[1]) * (refdataArray.length ? refdataArray[additionalData[1]] : additionalData[1]), 2),
			];
		}

		return [
			NumberHandler.floatFormatter(parseFloat(originData[0]) * (refdataArray.length ? refdataArray[additionalData] : additionalData), 2),
			NumberHandler.floatFormatter(parseFloat(originData[1]) * (refdataArray.length ? refdataArray[additionalData] : additionalData), 2)
		];
	}

	if (Array.isArray(additionalData)) {
		return [
			NumberHandler.floatFormatter(parseFloat(originData) * (refdataArray.length ? refdataArray[additionalData[0]] : additionalData[0]), 2),
			NumberHandler.floatFormatter(parseFloat(originData) * (refdataArray.length ? refdataArray[additionalData[1]] : additionalData[1]), 2)
		];
	}

	return NumberHandler.floatFormatter(parseFloat(originData) * (refdataArray.length ? refdataArray[additionalData] : additionalData), 2);
};
const parseChartData = (
	{
		data,
		annotations,
		refline,
		refdata,
		EstimatedYear,
	},
	dataPeriod,
	dataRangeMode,
	dataRange,
	chart,
	singleOptions,
	apiData,
	growthRate,
	objDollarRate = null,
	colourTheme
) => {
	const apiHost = ('useApiHost' in singleOptions) ? singleOptions.useApiHost
		: false;
	const dataLblsEnabled = ('data_lbls_enabled' in singleOptions)
		? singleOptions.data_lbls_enabled : false;
	const stackLblsEnabled = ('stack_lbls_enabled' in singleOptions)
		? singleOptions.stack_lbls_enabled : true;
	const dataCount = _size(data);
	const arrUseDollar = _values(data)
		.map((item) => (item.use_dollar));
	const noPercentage = _values(data).filter((item) => (
		item.UnitRef?.includes('%')
	)).length === 0;

	let annotation = [];
	let categoriesData = getCategories(data, dataPeriod);
	let categories = categoriesData.categories;
	let dataRefdata = _map(refdata, (item) => item.Data);
	let dataRefdataMax = _max(dataRefdata);
	let dataRefdataMin = _min(dataRefdata);
	// let maxData        = Number.MIN_SAFE_INTEGER;
	let minData = Number.MAX_SAFE_INTEGER;
	let plotBands = [];
	let plotLines = [];
	let seriesData = [];
	let toAlignAxis = false;
	let yAxis = [];
	let shouldConvertCurrency = noPercentage && objDollarRate
		&& checkDiff(arrUseDollar);

	// refline
	for (let key in refline) {
		plotLines.push({
			value: refline[key].Data,
			color: '#ef3474',
			width: 1.5,
			label: {
				text: refline[key].ChineseAccount,
				style: { color: COLOUR.text[colourTheme] },
			},
		});
	}

	// categories
	// categories = categoriesData.categories;

	if (typeof dataRangeMode === 'number') {
		categories = categories.slice(Math.max(categories.length - dataRangeMode, 0));
	} else if (dataRangeMode === 'custom' && (dataRange.start || dataRange.end)) {
		let dataStartPos = categories.indexOf(dataRange.start),
			dataEndPos = categories.indexOf(dataRange.end);

		const start = (dataStartPos !== -1) ? dataStartPos : 0,
			end = (dataEndPos !== -1) ? dataEndPos + 1 : categories.length;

		categories = categories.slice(start, end);
	}

	// annotation
	const arrDataForAnno = (data && dataCount)
		? func.getTypeData(_values(data)[0], dataPeriod)
		: {};

	for (let key in annotations) {
		const arrData = annotations[key].Data;
		const firstDate = categories[0];
		const lastDate = categories[categories.length - 1];
		const arrAnnoKey = Object.keys(arrData).filter((date) => (
			date >= firstDate && date <= lastDate
		));

		let labels = [];

		for (let i = 0; i < arrAnnoKey.length; i++) {
			if (!_size(arrDataForAnno)) { break; }

			const annoDate = arrAnnoKey[i];
			const y = arrDataForAnno[annoDate];

			if (y === undefined) { continue; }

			const x = categories.indexOf(annoDate);
			const txt = arrData[annoDate].toString();
			const txtValue = Number(txt)

			let colour = 'rgb(0, 0, 0)';

			if (!isNaN(txtValue)) {
				if (txtValue > 0) {
					colour = '#ef5350';
				} else if (txtValue < 0) {
					colour = '#4abf70';
				}
			}

			labels[labels.length] = {
				align: (categories[categories.length - 1] - annoDate < 100)
					? 'right' : 'left',
				backgroundColor: (colour === 'rgb(0, 0, 0)') ? (annotation.length % 2)
					? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.6)' : colour,
				borderColor: colour,
				point: {
					xAxis: 0,
					yAxis: 0,
					x: x,
					y: y,
				},
				text: txt,
				useHTML: true,
			};
		}
		annotation[annotation.length] = {
			crop: false,
			draggable: '',
			labelOptions: {
				allowOverlap: true,
				overflow: 'allow',
				verticalAlign: (annotation.length % 2) ? 'top' : 'bottom',
				y: (annotation.length % 2) ? 0 : -20,
			},
			labels: labels,
		}
	}

	// series
	for (let key in data) {
		const {
			ChineseAccount,
			EnglishAccount,
			Order,
			Style,
			UnitRef,
			use_dollar,
		} = data[key];
		const dataChartType = getChartStyle(Style, singleOptions);
		const isCompareColumn = Style?.includes('ompare');
		const isStackedColumn = ('chart_type' in singleOptions
			&& singleOptions.chart_type === 'line') ? false
			: checkStacking(Style, singleOptions);
		const useDollar = (use_dollar === 'TWD') ? 'NTD' : use_dollar;

		let originData = func.getTypeData(data[key], dataPeriod);
		let showData = [];
		let dollarRate = null;
		let unit = UnitRef;
		originData = (originData && _size(originData)) ? originData : {};
		originData = checkData(originData, categoriesData.arr_estimate);
		shouldConvertCurrency = shouldConvertCurrency && !EnglishAccount.includes('EPS');

		if (shouldConvertCurrency) { // default: use USD
			dollarRate = func.getTypeData(objDollarRate.USA[useDollar], dataPeriod);
		}

		if (dataChartType === 'networkgraph') {
			seriesData[seriesData.length] = {
				type: dataChartType,
				name: EnglishAccount,
				data: originData,
				dataLabels: { enabled: true },
				marker: { enabled: true },
			};

			return { series: seriesData };
		}

		for (let i = 0; i < categories.length; i++) {
			const category = categories[i];
			const categoryNoEstimate = category.includes('(')
				? category.substring(0, category.indexOf('(')) : category;
			const categoryEstimate = `${categoryNoEstimate}(f)`;

			let value = originData[category] ? originData[category] : originData[categoryNoEstimate]
				? originData[categoryNoEstimate] : originData[categoryEstimate] ?? null;

			if (dollarRate) {
				unit = '百萬美元';

				if (value !== null) {
					let rate = dollarRate[category];

					if (!rate) {
						const time_2015 = DateTimeHandler.getDateTime('2015');
						const time_category = DateTimeHandler.getDateTime(category);
						const isBefore = !isNaN(time_category) && time_category < time_2015;
						const arrDollarRate = _values(dollarRate);

						rate = (arrDollarRate.length === 0)
							? 1
							: arrDollarRate[isBefore ? 0 : arrDollarRate.length - 1];

					}

					value *= rate;

					if (/*useDollar === 'INR' ||*/ useDollar === 'JPY' || useDollar === 'KRW') {
						value *= 1000;
					}
				}
			}

			let dateData = parseSeriesData(
				value,
				category,
				dataChartType,
				singleOptions
			);
			dateData = (dateData === undefined || (isNaN(dateData)
				&& typeof dateData !== 'object')) ? null : dateData;

			const zDateData = dateData ? dateData : 0;

			dateData = (isStackedColumn && !dateData) ? 0 : dateData;

			if (minData > zDateData) { minData = zDateData; }

			if (EstimatedYear) {
				const idx = categories.indexOf(EstimatedYear);

				if (plotBands.length === 0) {
					plotBands = [{
						from: idx,
						to: categories.length,
						color: 'rgba(115, 162, 255, .2)',
					}];
				}

				if (growthRate !== undefined && i >= idx && dateData !== null) {
					dateData *= (1 + growthRate / 100);
				}
			}

			showData.push(dateData);
		}
		
		const nullCount = showData.filter((item) => (item === null || item === undefined)).length;

		if (dataChartType === 'line' && (showData.length === 1 || (showData.length > 1 && nullCount > 0))) {
			const isConnectNulls = singleOptions?.connectNulls;

			showData = showData.map((item, idx) => {
				const itemWithMarker = { y: item, marker: { enabled: true } };

				if (showData.length - nullCount === 1) { return itemWithMarker; }

				if (isConnectNulls) { return item; }

				const next = (idx === showData.length - 1) ? false : showData[idx + 1];
				const prev = (idx === 0) ? false : showData[idx - 1];

				switch (idx) {
					case 0:
						if (item && (next === null || next === undefined)) { return itemWithMarker; }
						break;

					case showData.length - 1:
						if (item && (prev === null || prev === undefined)) { return itemWithMarker; }
						break;

					default:
						if (item && (next === null || next === undefined)
							&& (prev === null || prev === undefined)) { return itemWithMarker; }
						break;
				}

				return item;
			});
		}

		// yAxis
		if (!(yAxis.find((y) => (y.title.text === unit)))) {
			let dataYAxis = {
				id: `y-${yAxis.length}`,
				title: { text: unit },
				maxPadding: 0.05,
				minPadding: 0.05,
				startOnTick: true,
				endOnTick: true,
			};

			if (yAxis.length !== 0) {
				dataYAxis.opposite = true;
				dataYAxis.plotLines = [{
					value: 0,
					color: COLOUR.grid[colourTheme],
					width: 1,
				}];
			} else {
				dataYAxis.plotLines = [{
					value: 0,
					color: COLOUR.title[colourTheme],
					width: 1,
				}];
			}

			yAxis.push(dataYAxis);
		}

		if (yAxis.length > 0) {
			yAxis[0] = {
				...yAxis[0],
				plotLines: [...yAxis[0].plotLines, ...plotLines],
			};
		}

		if (!toAlignAxis && (dataChartType === 'area' || dataChartType === 'column')
			&& yAxis.length > 1) {
			toAlignAxis = true;
		}

		const seriesDataYAxis = (yAxis.length > 1)
			? yAxis.indexOf(yAxis.find((y) => (y.title.text === unit)))
			: 0;

		let seriesDataSetting = {
			id: key,
			zIndex: (dataChartType === 'column') ? 1 : 2,
			name: ChineseAccount,
			type: dataChartType,
			data: showData,
			dataLabels: {
				enabled: dataChartType === 'pie' || apiHost || dataLblsEnabled,
			},
			colorByPoint: dataChartType === 'pie',
			yAxis: seriesDataYAxis,
			showInNavigator: seriesData.length ? true : false,
			showInLegend: true,
			unit: unit,
			order: Order,
		};

		if (isCompareColumn) {
			const arrColour = [
				Highcharts
					.color(COLOUR.series[colourTheme][3])
					.setOpacity(0.6)
					.get(),
				Highcharts
					.color(COLOUR.series[colourTheme][1])
					.setOpacity(0.9)
					.get(),
				Highcharts
					.color(COLOUR.series[colourTheme][2])
					.setOpacity(1)
					.get(),
			];

			seriesDataSetting.color = arrColour[Order - 1];
			seriesDataSetting.pointPadding = 0.05 * (Math.pow(Order, 2) - ((Order > 2)
				? 1 : 0));
		}

		if (isStackedColumn) {
			seriesDataSetting.stacking = true;
			yAxis[seriesDataYAxis].minPadding = 0;
			yAxis[seriesDataYAxis].maxPadding = 0;

			if (apiHost) {
				seriesDataSetting.dataLabels = {
					...seriesDataSetting.dataLabels,
					formatter: function () {
						let value = NumberHandler.comma(NumberHandler.getUnitNumber(
							this.y,
							(isStackedColumn === 'percent') ? 0 : false
						));
						value = (value === '0') ? '' : `${value} seriesDataSetting.unit`;

						return value;
					}
				};
			}

			yAxis[seriesDataYAxis].stackLabels = {
				enabled: stackLblsEnabled,
				formatter: function () {
					return NumberHandler.comma(NumberHandler.getUnitNumber(this.total));
				},
			};

			if (isStackedColumn === 'percent') {
				yAxis[seriesDataYAxis].stackLabels = { enabled: false };
			}
		}

		if (dataChartType === 'line') {
			seriesDataSetting.dashStyle = getDashStyle(Style, Order);
		}

		seriesData.push(seriesDataSetting);

		if (!singleOptions || !singleOptions.additional_data) { continue; }

		const additionalData = singleOptions.additional_data.find((d) => (
			d.linked.includes(key)
		));

		if (!additionalData) { continue; }

		let refdataArray = [];
		let calcByApi = ('calcByApi' in additionalData)
			? additionalData.calcByApi : false;
		let arrAdditionalKey = [];
		let arrAdditionalData = [];

		if (calcByApi) {
			arrAdditionalKey = Object.keys(apiData ?? []);
			arrAdditionalData = _values(apiData ?? []);

			let additionalDate = [];

			for (let additionalKey in arrAdditionalData) {
				additionalDate = additionalDate.concat(
					Object.keys(arrAdditionalData[additionalKey].Data)
				);
			}

			categories = _intersection(categories, additionalDate);
		} else {
			arrAdditionalData = additionalData.data;

			if (arrAdditionalData.length) {
				let refdataDiff = Math.abs(dataRefdataMax - dataRefdataMin)
					/ arrAdditionalData.length;

				for (let i = 0; i <= arrAdditionalData.length; i++) {
					refdataArray.push(dataRefdataMin + i * refdataDiff);
				}
			}
		}

		let useRefdata = ('isCalc' in additionalData && additionalData.isCalc)
			? refdataArray : [];

		for (let i = 0; i < arrAdditionalData.length; i++) {
			let addSeriesName;

			showData = [];

			for (let j = 0; j < categories.length; j++) {
				const category = categories[j];

				let additionalDataValue = Array.isArray(arrAdditionalData[i])
					? arrAdditionalData[i]
					: (category in arrAdditionalData[i].Data)
						? arrAdditionalData[i].Data[category]
						: 0;
				let dateData = parseAdditionalData(
					originData[category],
					additionalDataValue,
					useRefdata
				);

				showData.push(dateData);
			}

			const isAdditionalDataStacking = checkStacking(
				additionalData.type,
				singleOptions
			);

			if (!isAdditionalDataStacking) {
				let fillNullDataIndex = showData.length;

				for (let i = showData.length - 1; i >= 0; i--) {
					if (showData[i]) {
						fillNullDataIndex = i + 1;

						break;
					}
				}

				if (fillNullDataIndex !== showData.length && fillNullDataIndex > 0) {
					for (let i = fillNullDataIndex; i < showData.length; i++) {
						showData[i] = showData[i - 1];
					}
				}
			}

			if (additionalData.type === 'arearange') {
				addSeriesName = (('isCalc' in additionalData && additionalData.isCalc)
					? NumberHandler.floatFormatter(refdataArray[arrAdditionalData[i][0]], 1)
					+ '-' + NumberHandler.floatFormatter(refdataArray[arrAdditionalData[i][1]], 1)
					: arrAdditionalData[i][0] + '-' + arrAdditionalData[i][1]) + '倍';
			} else {
				addSeriesName = arrAdditionalData[i].ChineseAccount;
			}

			let additionalSeriesDataSetting = {
				id: arrAdditionalKey[i],
				zIndex: 1,
				name: addSeriesName,
				type: getChartStyle(additionalData.type, singleOptions),
				data: showData,
				dataLabels: { enabled: false },
				yAxis: seriesDataYAxis,
				showInNavigator: seriesData.length ? true : false,
				unit: UnitRef,
				threshold: (additionalData.type === 'area') ? null : 0,
				order: arrAdditionalData.length - i,
				noShowInTooltips: ('noShowInTooltips' in additionalData)
					? additionalData.noShowInTooltips : false,
			}

			if (isAdditionalDataStacking) {
				additionalSeriesDataSetting.stacking = true;
				yAxis[seriesDataYAxis].minPadding = 0;
				yAxis[seriesDataYAxis].maxPadding = 0;

				if (calcByApi) {
					additionalSeriesDataSetting.dataLabels = { enabled: true };
				}
			}

			seriesData.push(additionalSeriesDataSetting);
		}

		seriesData = seriesData.filter((d) => (d.id !== key));
	}

	if (toAlignAxis) {
		for (let i = 0; i < yAxis.length; i++) {
			if (minData <= 0) {
				yAxis[i] = { ...yAxis[i], startOnTick: false, endOnTick: false };
			}
		}
	}

	if (chart) {
		if (seriesData.find((d) => (d.stacking))) {
			while (chart.series.length > 0) { chart.series[0].remove(false); }

			for (let i = 0; i < seriesData.length; i++) {
				chart.addSeries({}, false);
			}
		} else {
			const diff = chart.series.length - seriesData.length;

			if (diff > 0) {
				while (chart.series.length > seriesData.length) {
					chart.series[seriesData.length].remove(false);
				}
			} else if (diff < 0) {
				for (let i = chart.series.lengthd; i < seriesData.length; i++) {
					chart.addSeries({}, false);
				}
			}
		}
	}

	seriesData = ArrayHandler.sorter(seriesData, 'order');

	return {
		annotations: annotation,
		categories: categories,
		plot_bands: plotBands,
		series: seriesData,
		y_axis: yAxis,
	}
};

const checkStacking = (style, singleOptions) => {
	const type = style?.toLowerCase();

	if (type === 'stackedcolumn') { return 'normal'; }

	if (type === 'stackedpercentcolumn') { return 'percent'; }

	return (singleOptions && 'stacking' in singleOptions) ? singleOptions.stacking
		: false;
};
const getCategories = (data, dataPeriod, isCombine = false) => {
	let categories = [];

	for (let key in data) {
		const arrData = func.getTypeData(data[key], dataPeriod);

		if (!arrData || !_size(arrData)) { continue; }

		let arrKey = Object.keys(arrData);

		categories = categories.concat(arrKey);
	}

	categories = DataHandler.duplicatesRemover(categories);

	let arrCheck = categories.map((category) => (category.replace('(f)', '')));
	arrCheck = arrCheck.filter((item, idx) => (arrCheck.indexOf(item) !== idx));
	categories = categories.map((category) => {
		let categoryUpdated = category;

		arrCheck.forEach((str) => {
			if (category.includes(str)) { categoryUpdated = `${str}(e)`; }
		});

		return categoryUpdated;
	});
	categories = DataHandler.duplicatesRemover(categories).sort();
	
	return { categories: categories, arr_estimate: arrCheck };
};
const parseChartOptions = (chartData) => {
	const {
		annotations,
		api_fetch_data,
		categories,
		colour_theme,
		is_mobile,
		option,
		plot_bands,
		series,
		title,
		y_axis,
	} = chartData;
	const isArearange = series.find((d) => (d.type === 'arearange')) !== undefined;
	const isEstimated = plot_bands && plot_bands.length > 0;
	const isNetworkgraph = series.find((d) => (d.type === 'networkgraph')) !== undefined;
	const isPie = series.find((d) => (d.type === 'pie')) !== undefined;
	const isColumn = series.find((d) => (d.type === 'column')) !== undefined;

	let colours = chartData.colours || COLOUR.series[colour_theme];

	if (isArearange && api_fetch_data && 'colors' in api_fetch_data) {
		colours = COLOUR.arearange[colour_theme];
	} else if (isPie && option && 'colors' in option) {
		colours = option.colors;
	}

	let options = {
		title: { text: title },
		colors: colours,
		tooltip: {
			enabled: !(isPie || isNetworkgraph),
			shared: (api_fetch_data && 'tooltip_shared' in api_fetch_data)
				? api_fetch_data.tooltip_shared : true
		},
		series: series,
		legend: { enabled: isPie || series.length > 1 },
		credits: { enabled: option ? !option.no_credit : true },
	};

	if (annotations && annotations.length > 0) {
		options.annotations = annotations;
	}

	if (categories) {
		options.xAxis = {
			id: 'x',
			categories: categories,
			labels: { allowOverlap: isEstimated },
			plotBands: plot_bands,
			tickPositioner: function () {
				const ext = this.getExtremes();
				const xMax = Math.round(ext.max);
				const xMin = Math.round(ext.min);

				return getXAxis(categories, xMax, xMin, this.width);
			},
		};
	}

	if (isNetworkgraph) { options.chart = { height: is_mobile ? 400 : 500 }; }

	if (y_axis) { options.yAxis = y_axis; }

	if (isColumn) {
		options.series = [
			...options.series,
			{
				id: 'proportion_pie',
				type: 'pie',
				name: '比重',
				data: [],
				center: [70, 30],
				size: 100,
				innerSize: '70%',
				showInLegend: false,
				unit: '%',
				zIndex: 9,
				visible: false,
				dataLabels: {
					enabled: true,
					distance: -50,
					format: '{point.series.name}',
					style: { fontSize: '14px', zIndex: 9 },
					filter: {
						operator: '===',
						property: 'index',
						value: 0,
					},
					showInLegend: false,
				},
			},
		];
		options.plotOptions = {
			...options.plotOptions,
			pie: {
				dataLabels: {
					enabled: false,
					showInLegend: false,
				},
			}
		};
		options.tooltip = {
			useHTML: true,
			shared: true,
			formatter: function () {
				let tooltip = this.x ? `<span>${this.x}</span><br/>` : '';

				if (this.points) {
					for (var i = 0; i < this.points.length; i++) {
						const { color, point, series, y } = this.points[i];

						if (series.options.noShowInTooltips) { continue; }

						tooltip += '<br/>';

						const { name, userOptions } = series;
						const unit = userOptions.unit ?? '';

						if (series.type === 'arearange') {
							tooltip += `<span
								id=${series.chart.container.id}-tooltips-${series.index}
								class='tooltips-row'
							>
								<span style='color: ${color}'>\u25CF</span>

								<b>
									${name}: ${Highcharts.numberFormat(series.low, 2, '.', ',')}${unit} - ${Highcharts.numberFormat(point.high, 2, '.', ',')}${unit}
								</b>
							</span>`;
						} else if (userOptions.type === 'column' && userOptions.stacking) {
							tooltip += `<span>
								<span style='color: ${color}'>\u25CF</span>

								<b>
									${name}: ${NumberHandler.comma(NumberHandler.getUnitNumber(y))}${unit}
								</b>
							</span>`;
						} else {
							tooltip += `<span>
								<span style='color: ${color}'>\u25CF</span>

								<b>
									${name}: ${NumberHandler.comma(NumberHandler.floatFormatter(y, 2))}${unit}
								</b>
							</span>`;
						}
					}

				} else {
					const { color, name, series, y } = this.point;
					const unit = series.userOptions.unit;

					if (this.series.type === 'pie') {
						tooltip += `<span
							id=${series.chart.container.id}-tooltips-${series.index}
							class='tooltips-row'
						>
							<span style='color: ${color}'>\u25CF</span>

							<b>
								${name}: ${Highcharts.numberFormat(y, 2, '.', ',')}${unit ?? ''}
							</b>
						</span>`;
					} else {
						tooltip += `<span>
	 						<span style='color: ${color}'>\u25CF</span>

	 						<b>
	 							${series.name}: ${Highcharts.numberFormat((unit ? y : y * 100 / (this.total ?? 1)), (unit ? 0 : 2), '.', ',')}${unit ? `（${unit}）` : ''}
	 						</b>
	 					</span>`;
					}
				}

				return tooltip;
			},
		};
	}

	return options;
};
const parseCombinedSeriesOptions = (dataParsed) => {
	let options;
	let column = [];
	let year = [];
	let average = [];
	let proportion = [];

	// column
	column = dataParsed.series;

	// year
	year = dataParsed.categories;

	// average
	const columnData = column.map((item, key) => ({
		name: item.name, data: item.data
	}));

	for (let i = 0; i < columnData.length; i++) {
		for (let index in columnData[i].data) {
			if (typeof average[index] !== 'undefined') {
				average[index] += columnData[i].data[index] / columnData.length;
			} else {
				average[index] = 0
			}
		}
	}

	// proportion
	let proportionItems = [];
	let allDataTotal = 0;

	for (let i = 0; i < column.length; i++) {
		const currData = columnData[i].data.reduce((total, item) => (total + item), 0);

		proportionItems.push(currData);
		allDataTotal += currData;
	}

	for (let i = 0; i < column.length; i++) {
		proportion.push({
			name: columnData[i].name,
			y: (proportionItems[i] / allDataTotal) * 100,
		});
	}

	options = {
		title: {
			text: '',
			align: 'left',
		},
		xAxis: { categories: year },
		yAxis: {
			stackLabels: {
				enabled: true,
				formatter: function () {
					const value = NumberHandler.comma(NumberHandler.getUnitNumber(this.total));

					return (value === '0') ? '' : value;
				},
			},
		},
		plotOptions: {
			column: { stacking: 'normal' },
			pie: {
				dataLabels: {
					enabled: true,
					distance: -50,
					format: '{point.series.name}',
					style: { fontSize: '16px' },
					filter: {
						operator: '===',
						property: 'index',
						value: 0,
					},
					showInLegend: false,
				},
			},
		},
		series: [
			...column,
			{
				id: 'average_line',
				type: 'line',
				step: 'center',
				name: '平均',
				data: average,
				marker: { lineWidth: 2, fillColor: 'white' },
				showInLegend: false,
				unit: '',
				zIndex: 2,
			},
			{
				id: 'proportion_pie',
				type: 'pie',
				name: '比重',
				data: proportion,
				center: [75, 65],
				size: 100,
				innerSize: '70%',
				showInLegend: false,
				unit: '%',
				zIndex: 2
			},
		],
		tooltip: {
			useHTML: true,
			shared: true,
			formatter: function () {
				let tooltip = this.x ? `<span>${this.x}</span><br/>` : '';

				if (this.points) {
					for (var i = 0; i < this.points.length; i++) {
						if (!this.points[i].series.options.noShowInTooltips) {
							tooltip += '<br/>';

							if (this.points[i].series.type === 'arearange') {
								tooltip += `<span
										id=${this.points[i].series.chart.container.id}-tooltips-${this.points[i].series.index}
										class='tooltips-row'
									>
										<span style='color: ${this.points[i].color}'>\u25CF</span>
										<b>
												${this.points[i].series.name}: ${Highcharts.numberFormat(this.points[i].point.low, 2, '.', ',')}${this.points[i].series.userOptions.unit ?? ''} - ${Highcharts.numberFormat(this.points[i].point.high, 2, '.', ',')}${this.points[i].series.userOptions.unit ?? ''}
										</b>
									</span>`;
							} else if (this.points[i].series.userOptions.type === 'column' && this.points[i].series.userOptions.stacking) {
								tooltip += `<span>
										<span style='color: ${this.points[i].color}'>\u25CF</span>
										<b>
												${this.points[i].series.name}: ${NumberHandler.comma(NumberHandler.getUnitNumber(this.points[i].y))}${this.points[i].series.userOptions.unit ?? ''}
										</b>
									</span>`;
							} else {
								tooltip += `<span>
										<span style='color: ${this.points[i].color}'>\u25CF</span>
										<b>
												${this.points[i].series.name}: ${NumberHandler.comma(NumberHandler.floatFormatter(this.points[i].y, 2))}${this.points[i].series.userOptions.unit ?? ''}
										</b>
									</span>`;
							}
						}
					}
				} else if (this.series.type === 'pie') {
					tooltip += `<span
											id=${this.point.series.chart.container.id}-tooltips-${this.point.series.index}
											class='tooltips-row'
										>
											<span style='color: ${this.point.color}'>\u25CF</span>
											<b>
													${this.point.name}: ${Highcharts.numberFormat(this.point.y, 2, '.', ',')}${this.point.series.userOptions.unit ?? ''}
											</b>
										</span>`;
				} else {
					tooltip += `<span>
					<span style='color: ${this.point.color}'>\u25CF</span>
							<b>
								${this.point.series.name}: ${Highcharts.numberFormat((this.point.series.userOptions.unit ? this.point.y : this.point.y * 100 / (this.total ?? 1)), (this.point.series.userOptions.unit ? 0 : 2), '.', ',')}${this.point.series.userOptions.unit ? ` (${this.point.series.userOptions.unit})` : ''}
							</b>
						</span>`;
				}

				return tooltip;
			}
		}
	};

	return options;
};

const getChartOptions = (props, chart) => {
	const {
		title,
		fetch_data,
		api_fetch_data,
		dataPeriod,
		dataRangeMode,
		defaultRangeMode,
		dataRange,
		option,
		growth_rate,
		stock,
		is_mobile,
		colour_theme,
	} = props;
	const useApiFetchData = api_fetch_data ? api_fetch_data.data : null;
	const chartData = _values(fetch_data.data);
	const chartType = chartData[0]?.Style ?? '';
	const isCompareColumn = chartType.includes('ompare');
	const isPolar = option && 'chart_type' in option
		&& option.chart_type === 'polar';
	const isSolidgauge = chartType === 'solidgauge';
	const isTreemap = chartType === 'treemap';
	const isWaterfall = chartType === 'waterfall';
	const isVariwide = chartType === 'variwide';
	const isComboPieColumn = 'combo_pie_column' in option
		&& option.combo_pie_column;

	let options = { title: { text: title } };

	if (isPolar) {
		const stockNum = fetch_data.stock_number;

		let categories = [];
		let arrSeries = [];

		for (let i = 0; i < chartData.length; i++) {
			const { ChineseAccount, Data } = chartData[i];

			let series = null;
			let twse = '-';
			let otc = '-';

			if (Data && _size(Data)) {
				const { TWSE, OTC } = Data;

				series = (!isNaN(TWSE) && !isNaN(OTC)) ? (TWSE + OTC) / (stockNum.TWSE
					+ stockNum.OTC) * 100 : null;
				twse = !isNaN(TWSE) ? TWSE / stockNum.TWSE * 100 : null;
				twse = NumberHandler.floatFormatter(twse, 1);
				twse = `${twse}%`;
				otc = !isNaN(OTC) ? OTC / stockNum.OTC * 100 : null;
				otc = NumberHandler.floatFormatter(otc, 1);
				otc = `${otc}%`;
			}

			categories[categories.length] = `${ChineseAccount}<br/>(${twse}, ${otc})`;
			arrSeries[arrSeries.length] = series;
		}

		options = {
			...options,
			chart: {
				height: 500,
				polar: true,
			},
			plotOptions: {
				area: {
					fillOpacity: 0.3,
					marker: {
						states: {
							hover: {
								enabled: false,
							},
						},
					},
				},
				series: {
					dataLabels: {
						enabled: true,
						allowOverlap: true,
						overflow: 'allow',
						formatter: function () {
							const item = chartData.filter((item) => (
								this.x.includes(item.ChineseAccount)
							));

							let result = '';

							if (item.length > 0 && item[0].Data && _size(item[0].Data)) {
								const { TWSE, OTC } = item[0].Data;

								result = (!isNaN(TWSE) && !isNaN(OTC)) ? TWSE + OTC : '';
							}

							return result;
						},
						nullFormat: '',
						style: { fontSize: '10px' },
					},
				},
			},
			tooltip: { enabled: false },
			xAxis: {
				categories: categories,
				labels: {
					align: 'center',
					allowOverlap: true,
					style: {
						textAlign: 'center',
					},
					useHTML: true,
				},
				lineWidth: 0,
				tickmarkPlacement: 'on',
			},
			yAxis: {
				gridLineInterpolation: 'polygon',
				labels: { enabled: false },
				lineWidth: 0,
				min: 0,
				max: 100,
				plotBands: [
					{
						color: 'rgba(239, 52, 116, 0.3)',
						from: 10,
						to: 30,
					},
					{
						color: 'rgba(239, 83, 80, 0.2)',
						from: 30,
						to: 50,
					},
				],
				plotLines: [
					{
						value: 30,
						color: '#ef3474',
						dashStyle: 'Dash',
						width: 1.5,
					},
					{
						value: 50,
						color: '#ef5350',
						dashStyle: 'Dash',
						width: 1.5,
					},
					{
						value: 80,
						color: '#4abf70',
						dashStyle: 'Dash',
						width: 1.5,
					},
				],
			},
			series: [{
				type: chartType,
				data: arrSeries,
				showInLegend: false,
			}],
		};
	} else if (isSolidgauge) {
		options = {
			...options,
			chart: {
				type: 'solidgauge',
				height: `${120 * 0.5}%`,
				spacingBottom: 0,
			},
			yAxis: {
				min: 0,
				max: 100,
				lineWidth: 0,
				tickPositions: [],
			},
			legend: {
				className: 'solidgauge-legend',
				useHTML: true,
				labelFormatter: function () {
					return `<>
							<span
								class='d-inline-block margin-right-2 img-circle'
								style='background-color: ${this.userOptions.color};'
							>
							</span>

							<span
								class='d-inline-block'
								style='color: ${COLOUR.text[colour_theme]};'
							>
								${this.name}
							</span>
						</>`;
				},
				itemWidth: 100,
			},
			tooltip: {
				borderWidth: 0,
				backgroundColor: 'none',
				shadow: false,
				formatter: undefined,
				pointFormatter: function () {
					const { name, options } = this.series;

					return `<div class='solidgauge-tooltip'>
							<>${options.originData} ${options.unit}</>

							<>${name}</>
						</div>`;
				},
				positioner: function (labelWidth) {
					return {
						x: (this.chart.chartWidth - labelWidth) / 2,
						y: (this.chart.chartHeight / 2),
					};
				}
			},
			pane: { background: [] },
			series: [],
		};

		const count = chartData.length + 1;

		let arrData = chartData.map((item) => ({
			...item,
			data: _values(func.getTypeData(item, dataPeriod))[0],
		}));

		const sum = arrData.reduce((partialSum, a) => (partialSum + a.data), 0);

		arrData = arrData.map((item) => ({
			...item,
			data: sum ? (item.data / sum * 100) : 0,
		}));

		for (let i = 0; i < count - 1; i++) {
			const temp = arrData[i];
			const {
				ChineseAccount,
				data,
				EnglishAccount,
				UnitRef,
			} = temp;

			let colour = 'transparent';

			if (EnglishAccount.includes('Buy')) {
				colour = '#ef5350';
			} else if (EnglishAccount.includes('Neutral')) {
				colour = '#ffa500';
			} else if (EnglishAccount.includes('Sell')) {
				colour = '#4abf70';
			}

			options.pane.background.push({
				backgroundColor: Highcharts
					.color(colour)
					.setOpacity(0.3)
					.get(),
				borderWidth: 0,
				outerRadius: `${(count - i) / count * 120 * 0.9}%`,
				innerRadius: `${((count - (i + 1)) / count * 120 * 0.9) + 1}%`,
			});
			options.series.push({
				name: ChineseAccount,
				color: colour,
				data: [{
					color: Highcharts
						.color(colour)
						.setOpacity(arrData[i].data ? 100 : 0)
						.get(),
					radius: `${(count - i) / count * 120 * 0.9}%`,
					innerRadius: `${((count - (i + 1)) / count * 120 * 0.9) + 1}%`,
					y: data,
				}],
				dataLabels: { enabled: false },
				showInLegend: true,
				originData: _values(func.getTypeData(temp, dataPeriod))[0],
				unit: UnitRef,
			});
		}
	} else if (isTreemap || isVariwide) {
		const treemapVariwideData = chartData[0].Data;

		options = {
			...options,
			subtitle: {
				text: (props.model === 'SubIndustryProfitForecast_Matrix')
					? '點擊產業看個股' : '',
				style: {
					...CHART_EXPORTING.style,
					color: COLOUR.text[colour_theme],
				},
			},
			xAxis: {
				type: 'category',
				labels: {
					allowOverlap: false,
					useHTML: true,
				},
			},
			series: [{
				type: (props.model === 'SubIndustryProfitForecast_Matrix') ? 'column'
					: chartType,
				name: title,
				data: treemapVariwideData,
				dataLabels: { enabled: false },
				colorByPoint: true,
			}],
			legend: { enabled: false },
		};

		if (chartData[0].drilldown) {
			options.drilldown = chartData[0].drilldown;
		}
	} else if (isWaterfall) {
		let arrData = [...chartData].map((item) => {
			const { ChineseAccount, Data, UnitRef } = item;

			return { name: ChineseAccount, y: Data, unit: UnitRef };
		});
		arrData.splice(
			0,
			0,
			{ name: '營收', isIntermediateSum: true, color: COLOUR.series[colour_theme][0] }
		);
		arrData[arrData.length] = {
			name: '營業利益',
			isSum: true,
			color: COLOUR.series[colour_theme][0],
		};
		options = {
			...options,
			chart: { type: 'waterfall' },
			xAxis: { type: 'category' },
			tooltip: {
				formatter: function () {
					const { name, options, y } = this.point;

					return `<span>${name}: ${y} ${options.unit}</span>`;
				},
			},
			series: [{
				upColor: COLOUR.series[colour_theme][12],
				color: COLOUR.series[colour_theme][1],
				data: arrData,
				showInLegend: false,
			}],
		};
	} else {
		let dataParsed = parseChartData(
			fetch_data,
			dataPeriod,
			dataRangeMode || defaultRangeMode,
			dataRange,
			chart,
			option,
			useApiFetchData,
			growth_rate,
			fetch_data.dollar_rate,
			colour_theme
		);

		if (isComboPieColumn) {
			options = parseCombinedSeriesOptions(dataParsed);
		}

		let chartTitle = '';

		if (title) {
			const stockTitle = (option.noStock || title.includes('指數'))
				?
				''
				:
				`${stock.stock_name} - `;

			chartTitle = `${stockTitle}${title}`;
		}

		dataParsed = {
			...dataParsed,
			api_fetch_data: useApiFetchData,
			colour_theme: colour_theme,
			is_mobile: is_mobile,
			option: option,
			title: chartTitle,
		};
		options = parseChartOptions(dataParsed);

		if (isCompareColumn) {
			options.plotOptions = {
				column: {
					grouping: false,
					shadow: false,
					borderWidth: 0,
				}
			};
		}
	}

	return options;
};
const getChartStyle = (style, option = {}) => {
	let type = ('chart_type' in option) ? option.chart_type : style;

	if (type) {
		type = type.toLowerCase();
	} else {
		return 'pie';
	}

	if ('compare' in option && option.compare === 'percent') {
		return 'percentcompare';
	}

	if (type === 'dotline') { return 'line'; }

	if (type === 'comparecolumn' || type === 'stackedcolumn'
		|| type === 'stackedpercentcolumn') { return 'column'; }

	return type;
};
const getCustomSelection = (data, dataPeriod) => {
	let categories = [];

	for (let key in data) {
		const arrData = func.getTypeData(data[key], dataPeriod);

		if (arrData && _size(arrData)) {
			categories = categories.concat(Object.keys(arrData));
		}
	}

	categories = DataHandler.duplicatesRemover(categories).sort();

	return categories;
};

// xAxis
const getXAxis = (categories, xMax, xMin, width) => {
	if (_isEqual(categories, ['Q1', 'Q2', 'Q3', 'Q4'])) {
		return [0, 1, 2, 3];
	} else if (_isEqual(
		categories,
		['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']
	)) {
		return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
	}

	let positions = [];
	let unitWith = 100;

	if (categories.length && categories[0].length <= 4) {
		unitWith = 40;
	} else if (categories.length && categories[0].length <= 7) {
		unitWith = 80;
	}

	const labelNum = (width / unitWith);
	const dataLen = (xMax - xMin + 1);
	const xWidth = Math.ceil(dataLen / labelNum);
	const isDate = (num) => {
		const yRegex = /^\d{4}$/;
		const yQRegex = /^\d{4}Q[1-4]$/;
		// const yMRegex = /^\d{4}(?:0[1-9]|1[0-2])$/;
		const yMDRegex = /^\d{4}(?:[-])?(?:0[1-9]|1[0-2])(?:[-])?(?:0[1-9]|[12]\d|3[01])?$/;

		return yRegex.test(num) || yQRegex.test(num) || yMDRegex.test(num);
	}

	if (isDate(categories[0])) {
		let index = xMax;

		for (; index > xMin; index -= xWidth) { positions.push(index); }

		if (xMax !== xMin) {
			if ((index + xWidth / 2) < xMin) { positions.pop(); }

			positions.push(xMin);
		}

		return positions;
	}

	return 0;
};
const isSingleDataPeriod = (data) => (_size(data) ? 'Data' in _values(data)[0]
	: false);
const parseSeriesData = (
	pointData,
	category,
	dataChartType,
	option
) => {
	if (pointData !== 0 && !pointData) { return null; }

	const pointDataUpdated = Array.isArray(pointData) ? pointData
		: parseFloat(pointData);

	if (dataChartType === 'arearange') {
		if (Array.isArray(pointData)) {
			return [parseFloat(pointDataUpdated[0]), parseFloat(pointDataUpdated[1])];
		}

		const {
			default_area_range_line_pair,
			func_chart_type,
			lower_bound,
			upper_bound,
		} = option;

		let result = [pointDataUpdated, pointDataUpdated];

		if (default_area_range_line_pair) {
			result[1] *= default_area_range_line_pair;
		} else if (func_chart_type) {
			if (func_chart_type === 'per') {
				result[0] *= lower_bound;
				result[1] *= upper_bound;
			} else {
				result[0] = result[0] / lower_bound * 100;
				result[1] = result[1] / upper_bound * 100;
			}
		}

		return result;
	} else if (dataChartType === 'pie') {
		return {
			name: category,
			y: NumberHandler.floatFormatter(pointDataUpdated, 2),
		};
	}

	return pointDataUpdated;
};


export default {
	checkStacking,
	getCategories,
	getChartOptions,
	getChartStyle,
	getCustomSelection,
	getXAxis,
	isSingleDataPeriod,
	parseSeriesData,
}
